import React, { useState, useRef, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import {
  Send,
  X,
  User as UserIcon,
  Sun,
  Moon,
  Menu,
  LogOut,
} from 'lucide-react';
import {
  sendChatMessage,
  generateSessionId,
  createUserContext,
  formatChatMessage,
  formatFollowupQuestions,
} from './api/chatService';
import SourceDisplay from './components/chat/SourceDisplay.js';
import FormattedResponse from './components/chat/FormattedResponseV2';
import { useAuth } from './hooks/useAuth';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import { ForgotPassword, ResetPassword } from './components/auth/PasswordReset';
import AuthCallback from './pages/AuthCallback.tsx';

import {
  AppErrorBoundary,
  ChatErrorBoundary,
} from './components/ErrorBoundary';
import './utils/diagnostics'; // Import diagnostics to enable logging

// Enhanced error logging function for production monitoring
const logApplicationError = errorDetails => {
  // In production, send to monitoring service
  if (process.env.NODE_ENV === 'production') {
    try {
      // Example: Send to monitoring service like Sentry, DataDog, etc.
      fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...errorDetails,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      }).catch(() => {
        // Silently fail if monitoring service is unavailable
      });
    } catch {
      // Silently fail if error logging fails
    }
  }
};

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSidebar, setShowSidebar] = useState(false); // Default to not showing session info
  const [isDarkMode, setIsDarkMode] = useState(false); // Default to light mode for government site
  const [sessionId, setSessionId] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [highlightedSourceIndex, setHighlightedSourceIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const sourceDisplayRefs = useRef({});
  const navigate = useNavigate();
  const sidebarLogoutRef = useRef(null); // Refence to the logout button visible in sidebar

  // Get user data from authentication context
  const { user, logout, isAuthenticated } = useAuth();

  // Create userData object from authenticated user
  const userData = user ? {
    id: user.id || user.sub || 'authenticated-user',
    email: user.email || 'user@mass.gov',
    type: user.type || 'Authenticated User',
    role: user.role || 'DUA Staff',
    dept: user.department || 'Department of Unemployment Assistance',
  } : null;

  // Remove automatic redirect - let ProtectedRoute handle it

  // Initialize session on component mount
  useEffect(() => {
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // When the sidebar is visible, set the focus to the logout button
  useEffect(() => {
    if (showSidebar && sidebarLogoutRef.current) {
      // Put focus on the sidebar element
      sidebarLogoutRef.current.focus();
    }
  }, [showSidebar]); // This runs every time showSidebar changes

  const handleCardClick = cardType => {
    let question = '';
    switch (cardType) {
      case 'policy':
        question = 'What policy procedures and guidelines are available?';
        break;
      case 'workflow':
        question = 'How do I find workflow guidance for case determinations?';
        break;
      case 'documentation':
        question =
          'What documentation and reference materials can you help me find?';
        break;
    }
    setInputMessage(question);
  };

  const handleSendMessage = async e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    // If no message, no session, or content is loading, do nothing
    if (!inputMessage.trim() || !sessionId || isLoading) {
      return;
    }

    // Clear any previous API errors
    setApiError(null);

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('Processing your request...');

    // Enhanced loading progress simulation
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev < 30) {
          setLoadingMessage('Analyzing your question...');
          return prev + 2;
        } else if (prev < 60) {
          setLoadingMessage('Searching knowledge base...');
          return prev + 1;
        } else if (prev < 85) {
          setLoadingMessage('Generating response...');
          return prev + 0.5;
        } else {
          setLoadingMessage('Finalizing results...');
          return Math.min(prev + 0.2, 95);
        }
      });
    }, 500);

    try {
      // Create user context from userData
      const userContext = createUserContext(userData || {
        id: 'guest-user',
        email: 'user@mass.gov',
        type: 'Guest User',
        role: 'DUA Staff',
        dept: 'Department of Unemployment Assistance',
      });

      // Send message to backend API
      const apiResponse = await sendChatMessage(
        currentMessage,
        sessionId,
        userContext
      );

      // Complete the progress
      clearInterval(progressInterval);
      setLoadingProgress(100);
      setLoadingMessage('Complete!');

      // Format the response message with sources
      const botMessage = {
        ...formatChatMessage(apiResponse),
        sources: apiResponse.sources || [],
        followup_questions: formatFollowupQuestions(
          apiResponse.followup_questions || []
        ),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      // Clear progress interval on error
      clearInterval(progressInterval);
      setLoadingProgress(0);
      setLoadingMessage('');

      // Log error for monitoring
      logApplicationError({
        error_type: 'chat_api_error',
        error_message: error.message,
        session_id: sessionId,
        user_id: userData?.id,
        context: 'chat_message_send',
        stack_trace: error.stack,
      });

      // Set API error for display
      setApiError(error.message);

      const errorMessage = {
        id: Date.now() + 1,
        text:
          error.message ||
          "I apologize, but I'm having trouble processing your request right now. Please try again.",
        sender: 'bot',
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  // Handle citation clicks - scroll to and highlight the corresponding source
  const handleCitationClick = (sourceIndex, messageId) => {
    setHighlightedSourceIndex(sourceIndex);

    // Get the source display ref for this message
    const sourceDisplayRef = sourceDisplayRefs.current[messageId];
    if (sourceDisplayRef && sourceDisplayRef.scrollToSource) {
      sourceDisplayRef.scrollToSource(sourceIndex);
    }

    // Clear highlight after 3 seconds
    setTimeout(() => {
      setHighlightedSourceIndex(null);
    }, 3000);
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleLogout = async () => {
    try {
      // Don't navigate to /login - let the logout function handle the redirect
      await logout();
      // The logout function in AuthContext will redirect to Cognito logout
      // which will then redirect back to /login after successful logout
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // To generate the URL + parameters for accessibility link
  const buildAccessibilityURL = () => {  
    
    const accessibilityURL = 'https://www.mass.gov/info-details/commonwealth-of-massachusetts-executive-department-digital-accessibility-statement'
    const currentURL = window.location.href;
    
    const param_names = ['referring_site', 'referring_org', 'referring_org_sec', 'referring_url'];
    const param_values = ['duamentor', 'departmentofunemploymentassistance', 'eolwd', currentURL];

    return accessibilityURL + '?' +
      param_names[0] + '=' + param_values[0] + '&' +
      param_names[1] + '=' + param_values[1] + '&' +
      param_names[2] + '=' + param_values[2] + '&' +
      param_names[3] + '=' + currentURL;
    
  };


  // Mayflower color palette
  const colors = {
    // Primary Massachusetts colors
    primaryBlue: '#003479', // Mass.gov primary blue
    primaryNavy: '#14558f', // Massachusetts navy
    actionBlue: '#388bb4', // Action/link blue

    // Status colors
    successGreen: '#059e8e',
    warningOrange: '#d97706', // Darker, more professional orange
    errorRed: '#cc0000',

    // Neutral colors
    grayDark: '#2c3539',
    grayMedium: '#7d8084',
    grayLight: '#f0f3f6',
    white: '#ffffff',

    // Text colors
    textPrimary: '#2c3539',
    textSecondary: '#2c3539',

    // Dark mode specific colors
    darkBlue: '#6bb6ff', // Much lighter blue for better dark mode visibility
    darkActionBlue: '#87ceeb', // Even lighter for links in dark mode
    darkText: '#e5e7eb', // Light gray for better text visibility in dark mode
  };

  // Don't render chat interface if not authenticated
  if (!isAuthenticated || !userData) {
    return null;
  }

  return (
    <div
      className={`flex h-screen font-sans ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}
    >
      {/* Massachusetts Blue Header Bar */}
      <div
        className='fixed top-0 left-0 right-0 h-1 z-50'
        style={{ backgroundColor: colors.primaryBlue }}
      />

      {/* Sidebar */}
      {showSidebar && (
        <div
          className={`w-80 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col mt-1`}
        >
          {/* Sidebar Header */}
          <div
            className={`p-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <UserIcon
                  className='w-5 h-5'
                  style={{color: isDarkMode ? colors.darkBlue : colors.primaryBlue,}}
                />
                <span
                  className='font-semibold text-lg'
                  style={{color: isDarkMode ? colors.darkText : colors.textPrimary,}}
                >
                  Session Info
                </span>
              </div>
              <div className='flex items-center space-x-2'>
                <button
                  ref={sidebarLogoutRef} // Reference for setting focus
                  onClick={handleLogout}
                  className={`p-1 rounded hover:bg-gray-100 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900'}`}
                  title="Logout"
                >
                  <LogOut className='w-4 h-4' />
                </button>
                <button
                  onClick={() => setShowSidebar(false)}
                  className={`p-1 rounded hover:bg-gray-100 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900'}`}
                  title="Close sidebar"
                >
                  <X className='w-5 h-5' />
                </button>
              </div>
            </div>
          </div>

          {/* User Information */}
          <div className='p-6 space-y-4 flex-1'>
            <div className='space-y-3'>
              <div>
                <span
                  className='text-sm font-medium'
                  style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                >
                  ID:
                </span>
                <div
                  className='text-base font-mono'
                  style={{color: isDarkMode ? colors.darkText : colors.textPrimary,}}
                >
                  {userData.id}
                </div>
              </div>
              <div>
                <span
                  className='text-sm font-medium'
                  style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                >
                  Email:
                </span>
                <div>
                  <p>{userData.email}</p>
                </div>
              </div>
              <div>
                <span
                  className='text-sm font-medium'
                  style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                >
                  Type:
                </span>
                <div
                  className='text-base'
                  style={{color: isDarkMode ? colors.darkText : colors.textPrimary,}}
                >
                  {userData.type}
                </div>
              </div>
              <div>
                <span
                  className='text-sm font-medium'
                  style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                >
                  Role:
                </span>
                <div
                  className='text-base'
                  style={{color: isDarkMode ? colors.darkText : colors.textPrimary,}}
                >
                  {userData.role}
                </div>
              </div>
              <div>
                <span
                  className='text-sm font-medium'
                  style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                >
                  Department:
                </span>
                <div
                  className='text-base'
                  style={{color: isDarkMode ? colors.darkText : colors.textPrimary,}}
                >
                  {userData.dept}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className='flex-1 flex flex-col mt-1'>
        
        {/* Header */}
        <header
          className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} px-6 py-4 border-b shadow-sm`}
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-4'>
              {!showSidebar && (
                <button
                  onClick={() => setShowSidebar(true)}
                  className={`p-2 rounded hover:bg-gray-100 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600'}`}
                  title='Open sidebar'
                >
                  <Menu className='w-5 h-5' />
                </button>
              )}
              <div className='flex items-center space-x-4'>
                {/* DUA Logo - Replace with actual logo */}
                <div className='flex items-center'>
                  <img
                    src='/dua-logo.png'
                    alt='Massachusetts Department of Unemployment Assistance'
                    className='h-8'
                  />
                </div>
                <div className="border-l h-8 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}" />
                <div>
                  <h1
                    className='text-xl font-bold'
                    style={{color: isDarkMode ? colors.darkBlue : colors.primaryBlue,}}
                  >
                    DUA Mentor
                  </h1>
                  <p
                    className='text-xs'
                    style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                  >
                    Internal Staff Assistant
                  </p>
                </div>
              </div>
            </div>
            <div className='flex items-center space-x-2'>
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' : 'bg-gray-100 hover:bg-gray-200'}`}
                style={{ color: isDarkMode ? '#fbbf24' : colors.textSecondary }}
                title="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className='w-5 h-5' />
                ) : (
                  <Moon className='w-5 h-5' />
                )}
              </button>
              <button
                onClick={handleLogout}
                className={`p-2 rounded-lg transition-colors flex items-center space-x-2 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                title="Logout"
              >
                <LogOut className='w-5 h-5' />
                <span className='text-sm font-medium'>Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main class="contents" aria-live='polite'>        
          {/* Messages Area */}
          <div className='flex-1 overflow-y-auto'>
          <div className='max-w-4xl mx-auto p-6 space-y-6'>
            {/* No messages yet - start screen */}
            {messages.length === 0 && (
              <div className={`text-center py-12`} style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}>
                <h1 className='text-2xl font-bold mb-3' style={{color: isDarkMode ? colors.darkBlue : colors.primaryBlue,}}>
                  Welcome to DUA Mentor
                </h1>
                <p
                  className='text-lg mb-6 max-w-2xl mx-auto'
                  style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                >
                  Your internal assistant for DUA staff. Quickly access
                  procedural documents, policy references, and workflow guidance
                  to support your work.
                </p>
                <div
                  className={`grid md:grid-cols-3 gap-4 max-w-3xl mx-auto text-left`}
                >
                  {/* Policy Procedures button */}
                  <button
                    onClick={() => handleCardClick('policy')}
                    className={`p-4 rounded-lg border transition-all hover:shadow-md hover:scale-105 text-left ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                    title='Ask about policy procedures'
                    aria-describedby='policy-description'
                  >
                    <h2
                      className='font-semibold mb-2'
                      style={{color: isDarkMode ? colors.darkBlue : colors.primaryBlue,}}
                    >
                      Policy Procedures
                    </h2>
                    <p
                      id='policy-description'
                      className='text-sm'
                      style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                    >
                      Access current policies and procedural guidelines
                    </p>
                  </button>
                  {/* Workflow Guidance button */}
                  <button
                    onClick={() => handleCardClick('workflow')}
                    className={`p-4 rounded-lg border transition-all hover:shadow-md hover:scale-105 text-left ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                    title='Ask about workflow guidance'
                    aria-describedby='workflow-description'
                  >
                    <h2
                      className='font-semibold mb-2'
                      style={{color: isDarkMode ? colors.darkBlue : colors.primaryBlue,}}
                    >
                      Workflow Guidance
                    </h2>
                    <p
                      id='workflow-description'
                      className='text-sm'
                      style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                    >
                      Step-by-step guidance for case determinations
                    </p>
                  </button>
                  {/* Documentation button */}
                  <button
                    onClick={() => handleCardClick('documentation')}
                    className={`p-4 rounded-lg border transition-all hover:shadow-md hover:scale-105 text-left ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                    title='Ask about documentation'
                    aria-describedby='documentation-description'
                  >
                    <h2
                      className='font-semibold mb-2'
                      style={{color: isDarkMode ? colors.darkBlue : colors.primaryBlue,}}
                    >
                      Documentation
                    </h2>
                    <p
                      id='documentation-description'
                      className='text-sm'
                      style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                    >
                      Find relevant forms and reference materials
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* There are messages to display */}
            {messages.map(message => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {/* Message bubble (from user or chatbot) */}
                <div
                  className={`max-w-3xl px-4 py-3 rounded-lg ${
                    message.sender === 'user'
                      ? 'text-white'
                      : message.isError
                        ? `border ${isDarkMode ? 'bg-red-900 text-red-200 border-red-700' : 'bg-red-50 text-red-800 border-red-300'}`
                        : `${isDarkMode ? 'bg-gray-700 text-gray-100' : 'bg-white border border-gray-200 shadow-sm'}`
                  }`}
                  style={message.sender === 'user' ? { backgroundColor: colors.primaryBlue } : {}}
                >
                  {message.sender === 'user' ? (
                    <p className='whitespace-pre-wrap leading-relaxed'>
                      {message.text}
                    </p>
                  ) : (
                    <div>
                      <FormattedResponse
                        text={message.text}
                        sources={message.sources}
                        isDarkMode={isDarkMode}
                        colors={colors}
                        onCitationClick={sourceIndex =>
                          handleCitationClick(sourceIndex, message.id)
                        }
                      />
                      <SourceDisplay
                        ref={el => (sourceDisplayRefs.current[message.id] = el)}
                        sources={message.sources}
                        isDarkMode={isDarkMode}
                        colors={colors}
                        highlightedSourceIndex={highlightedSourceIndex}
                      />
                      {message.followup_questions &&
                        message.followup_questions.length > 0 && (
                          <div className='mt-4 pt-3 border-t border-gray-200'>
                            <div
                              className='text-sm font-medium mb-2'
                              style={{color: isDarkMode ? colors.darkText : colors.textPrimary,}}
                            >
                              Related Questions:
                            </div>
                            <div className='space-y-2'>
                              {message.followup_questions
                                .slice(0, 4)
                                .map(fq => (
                                  <button
                                    key={fq.id}
                                    onClick={() => {
                                      setInputMessage(fq.question);
                                      handleSendMessage();
                                    }}
                                    className={`block w-full text-left text-sm p-2 rounded border transition-colors hover:bg-opacity-10 hover:bg-gray-500 ${
                                      isDarkMode
                                        ? 'border-gray-600 text-gray-300 hover:text-white'
                                        : 'border-gray-200 text-gray-600 hover:text-gray-900'
                                    }`}
                                    title='Ask suggested follow up question'
                                  >
                                    {fq.question}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Message loading indicator */}
            {isLoading && (
              <div className='flex justify-start'>
                <div
                  className={`px-4 py-4 rounded-lg max-w-md ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}
                >
                  <div className='space-y-3'>
                    <div className='flex items-center space-x-3'>
                      <div className='flex space-x-1'>
                        <div
                          className='w-2 h-2 rounded-full animate-bounce'
                          style={{ backgroundColor: colors.primaryBlue }}
                        />
                        <div
                          className='w-2 h-2 rounded-full animate-bounce'
                          style={{
                            backgroundColor: colors.primaryBlue,
                            animationDelay: '0.1s',
                          }}
                        />
                        <div
                          className='w-2 h-2 rounded-full animate-bounce'
                          style={{
                            backgroundColor: colors.primaryBlue,
                            animationDelay: '0.2s',
                          }}
                        />
                      </div>
                      <span
                        className='text-sm font-medium'
                        style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                      >
                        {loadingMessage || 'Processing your request...'}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className='w-full'>
                      <div
                        className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}
                      >
                        <div
                          className='h-2 rounded-full transition-all duration-500 ease-out'
                          style={{
                            backgroundColor: colors.primaryBlue,
                            width: `${loadingProgress}%`,
                          }}
                        />
                      </div>
                      <div
                        className='flex justify-between text-xs mt-1'
                        style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
                      >
                        <span>Processing...</span>
                        <span>{Math.round(loadingProgress)}%</span>
                      </div>
                    </div>

                    {/* Helpful tip for long requests */}
                    {loadingProgress > 70 && (
                      <div
                        className='text-xs p-2 rounded'
                        style={{
                          backgroundColor: isDarkMode
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'rgba(59, 130, 246, 0.05)',
                          color: isDarkMode
                            ? colors.darkActionBlue
                            : colors.actionBlue,
                        }}
                      >
                        💡 Complex queries may take up to 90 seconds to process
                        completely.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
          </div>

          {/* Input Area */}
          <div
          className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t shadow-lg`}
        >
          <div className='max-w-4xl mx-auto p-6'>
            {/* API Error Display */}
            {apiError && (
              <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <X className='h-5 w-5 text-red-400' />
                  </div>
                  <div className='ml-3'>
                    <p className='text-sm text-red-800'>
                      <strong>Connection Error:</strong> {apiError}
                    </p>
                  </div>
                  <div className='ml-auto pl-3'>
                    <button
                      onClick={() => setApiError(null)}
                      className='inline-flex text-red-400 hover:text-red-600'
                      title='Close error message'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className='flex space-x-3'>
              <textarea
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder='Ask about policies, procedures, workflows, or documentation...'
                /*disabled={isLoading}*/
                rows={1}
                className={`flex-1 px-4 py-3 border rounded-lg resize-none transition-colors focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50 ${
                  isDarkMode
                    ? 'bg-gray-700 text-white placeholder-gray-400 border-gray-600'
                    : 'bg-white text-gray-900 placeholder-gray-500 border-gray-300'
                }`}
                style={{
                  minHeight: '52px',
                  maxHeight: '120px',
                  focusRingColor: colors.primaryBlue,
                }}
              />
              <button
                onClick={handleSendMessage}
                //disabled={isLoading || !inputMessage.trim()}
                className='px-6 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center'
                style={{ backgroundColor: colors.primaryBlue }}
                title='Send message'
              >
                <Send className='w-5 h-5' />
              </button>
            </div>
            <p
              className='text-xs mt-2 text-center'
              style={{color: isDarkMode ? colors.darkText : colors.textSecondary,}}
            >
              Internal staff tool - Massachusetts Department of Unemployment
              Assistance
            </p>
          </div>
          </div>
        </main>
        
        {/* Footer required for Digital Accessibility Statement*/}
        <footer className='bg-[#F2F2F2] pt-4 pb-4 pl-12'>
          <a href={buildAccessibilityURL()} className='hover:underline'>Digital Accessibility Statement</a>
        </footer>

      </div>
      
    </div>
  );
};

const App = () => {
  return (
    <AppErrorBoundary
      name='App'
      onError={logApplicationError}
      userId='system'
      sessionId='app-session'
    >
      <Router>
        <Routes>
          {/* Authentication Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Protected Chat Interface */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <ChatErrorBoundary
                  name='ChatInterface'
                  onError={logApplicationError}
                >
                  <ChatInterface />
                </ChatErrorBoundary>
              </ProtectedRoute>
            }
          />
          
          {/* Default route - let frontend decide based on auth */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ChatErrorBoundary
                  name='ChatInterface'
                  onError={logApplicationError}
                >
                  <ChatInterface />
                </ChatErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AppErrorBoundary>
  );
};

export default App;
